import {
  detectPushClientGate,
  permissionToToggleState,
  urlBase64ToUint8Array,
  type PushClientStatus
} from './client-support';

const SUBSCRIBE_URL = '/api/push/subscribe';

export type BrowserPushApi = {
  standalone: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  notificationPermission: NotificationPermission | 'unsupported';
  hasNotification: boolean;
  hasPushManager: boolean;
  hasServiceWorker: boolean;
  vapidPublicKey?: string;
  getRegistration: () => Promise<ServiceWorkerRegistration | undefined>;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: (
    registration: ServiceWorkerRegistration,
    applicationServerKey: Uint8Array
  ) => Promise<PushSubscription>;
  getSubscription: (
    registration: ServiceWorkerRegistration
  ) => Promise<PushSubscription | null>;
  postSubscription: (subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }) => Promise<boolean>;
  deleteSubscription: (endpoint: string) => Promise<void>;
  getServerSubscribed: (endpoint: string) => Promise<boolean>;
};

export function readBrowserPushApi(): BrowserPushApi | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true);
  return {
    standalone,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    notificationPermission:
      'Notification' in window ? Notification.permission : 'unsupported',
    hasNotification: 'Notification' in window,
    hasPushManager: 'PushManager' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    vapidPublicKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
    getRegistration: () => navigator.serviceWorker.getRegistration(),
    requestPermission: () => Notification.requestPermission(),
    subscribe: (registration, applicationServerKey) =>
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource
      }),
    getSubscription: registration => registration.pushManager.getSubscription(),
    postSubscription: async subscription => {
      const response = await fetch(SUBSCRIBE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });
      return response.ok;
    },
    deleteSubscription: async endpoint => {
      await fetch(SUBSCRIBE_URL, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint })
      });
    },
    getServerSubscribed: async endpoint => {
      const response = await fetch(
        `${SUBSCRIBE_URL}?endpoint=${encodeURIComponent(endpoint)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        return false;
      }
      const data = (await response.json()) as { subscribed?: boolean };
      return data.subscribed === true;
    }
  };
}

export async function readPushClientStatus(
  api: BrowserPushApi
): Promise<PushClientStatus> {
  const gate = detectPushClientGate(api);
  if (gate !== 'ready') {
    return gate;
  }
  if (!api.vapidPublicKey) {
    return 'unsupported';
  }
  const registration = await api.getRegistration();
  if (!registration) {
    return 'unsupported';
  }
  if (api.notificationPermission === 'denied') {
    return 'denied';
  }
  const subscription = await api.getSubscription(registration);
  if (!subscription) {
    return 'off';
  }
  const subscribed = await api.getServerSubscribed(subscription.endpoint);
  return permissionToToggleState(api.notificationPermission, subscribed);
}

export async function enablePushNotifications(
  api: BrowserPushApi
): Promise<PushClientStatus> {
  const gate = detectPushClientGate(api);
  if (gate !== 'ready') {
    return gate;
  }
  if (!api.vapidPublicKey) {
    return 'unsupported';
  }
  const registration = await api.getRegistration();
  if (!registration) {
    return 'unsupported';
  }
  const permission = await api.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'off';
  }
  const subscription = await api.subscribe(
    registration,
    urlBase64ToUint8Array(api.vapidPublicKey)
  );
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return 'off';
  }
  const ok = await api.postSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
  });
  return ok ? 'on' : 'off';
}

export async function disablePushNotifications(
  api: BrowserPushApi
): Promise<PushClientStatus> {
  const registration = await api.getRegistration();
  const subscription = registration
    ? await api.getSubscription(registration)
    : null;
  if (subscription) {
    await api.deleteSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  }
  return 'off';
}

export async function unsubscribePushOnSignOut(
  api: Pick<
    BrowserPushApi,
    'getRegistration' | 'getSubscription' | 'deleteSubscription'
  >
): Promise<void> {
  try {
    const registration = await api.getRegistration();
    if (!registration) {
      return;
    }
    const subscription = await api.getSubscription(registration);
    if (!subscription) {
      return;
    }
    await api.deleteSubscription(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  } catch {
    // Sign-out cleanup is best-effort.
  }
}
